import { sendTextToChat, sendFileToChat } from '~/utils/auto-chat-wrapper';
import { base64UrlToJson } from '~/utils/base64-utils';

export const state = () => ({
  autoChatSentStatus: {
    transactionDetail: false,
    VPtransaction: false,
    inAppReview: false,
    payload: null,
  },
});

export const getters = {
  getAutoChatSentStatus(state) {
    return state.autoChatSentStatus;
  },
};

export const mutations = {
  setAutoChatSentStatus(state, payload = { autoChatType: null, status: null }) {
    state.autoChatSentStatus[payload.autoChatType] = payload.status;
  },
};

export const actions = {
  /**
   *
   * @param {*} payload = JSON-URI-ENCODED
   * payload is sent from route.query.chat
   * query.chat is a URI Encoded JSON Object
   * use the base64ToJSON method above to parse the uri into JSON object
   * after parse, setup autochat manually the given method (sendTextToChat/sendFileToChat)
   * see actions.initTransactionDetailAutoChat for example
   * MAKE SURE to block the chat method if condition are not met
   */
  initZopimAutoChatOnClientChat({ dispatch }, payload) {
    dispatch('zopim/autoChat/initPreviousAutoChat', payload, { root: true });
    dispatch('zopim/autoChat/initTransactionDetailAutoChat', payload, { root: true });
    dispatch('zopim/autoChat/initVPTransactionAutoChat', payload, { root: true });
    if (payload.from === 'InAppReviewSheet') {
      dispatch('zopim/autoChat/initInAppReviewAutoChat', payload, { root: true });
    }
  },

  /**
   * @param {object} context
   * @param {object} autoChatPayload
   * @param {string | object} autoChatPayload.payload
   * @param {string} autoChatPayload.type
   * @param {string} autoChatPayload.from
   */
  initPreviousAutoChat({ state, commit }, autoChatPayload) {
    const { autoChatSentStatus } = state;

    if (autoChatPayload.type !== 'jsonBase64UrlEncoded' || autoChatSentStatus.previousAutoChat) return;

    const jsonFromPayload = base64UrlToJson(autoChatPayload.payload);
    if (!jsonFromPayload.ticketId) return;

    let autoChatText = `\nTicket ID sebelumnya: ${jsonFromPayload.ticketId}\n`;
    if (jsonFromPayload.previousAutoChat) autoChatText += `${jsonFromPayload.previousAutoChat}\n`;

    sendTextToChat(autoChatText);
    commit('setAutoChatSentStatus', { autoChatType: 'previousAutoChat', status: true, payload: jsonFromPayload });
  },

  /**
   * This action would only send the text only (old phone number and new phone number)
   * Ever since we migrated to Zendesk Agent Workspace, we cannot send the texts immediately with the all files
   * This action will only be invoked when the user begins chat
   */
  async initChangePhoneNumberAutoChat({ dispatch }) {
    const messageResponse = await dispatch('change-phone-number/createAutomaticMessage', null, { root: true });
    if (!messageResponse) return;
    sendTextToChat(messageResponse.text, { display_name: messageResponse.name });
  },

  /**
   * This action will send the Change Phone Number documents (KTP, Selfie, and Supporting Doc)
   * Ever since we migrated to Zendesk Agent Workspace, these files cannot be immediately sent with the texts
   * This action will only be invoked when the agent joins the chat
   * The form data and the documents will be flushed from the local storage when this action is finished
   */
  async initChangePhoneNumberDocumentAutoUpload({ dispatch }) {
    const documentResponse = await dispatch('change-phone-number/createAutomaticDocumentMessages', null, {
      root: true,
    });
    if (!documentResponse) return;
    sendFileToChat(documentResponse.ktp);
    sendFileToChat(documentResponse.selfie);
    sendFileToChat(documentResponse.doc);

    dispatch('change-phone-number/flushMessageAndLocalStorage', null, { root: true });
  },

  initTransactionDetailAutoChat({ state, commit }, autoChatPayload) {
    const { autoChatSentStatus } = state;

    if (autoChatPayload.type !== 'jsonBase64UrlEncoded' || autoChatSentStatus.transactionDetail) return;

    const jsonFromPayload = base64UrlToJson(autoChatPayload.payload);
    if (!jsonFromPayload.productType || jsonFromPayload.productType === 'In App Review') return;

    let autoChatText =
      '\n' +
      `Ini detail transaksi ${jsonFromPayload.productType} saya ya:\n` +
      '\n' +
      `Tanggal transaksi: ${jsonFromPayload.createdTime || '-'}\n` +
      `Invoice: ${jsonFromPayload.paymentId || '-'}\n` +
      `Status: ${jsonFromPayload.status || '-'}\n` +
      `Metode pembayaran: ${jsonFromPayload.paymentMethod || '-'}\n`;
    if (jsonFromPayload.productName) autoChatText += `Nama produk: ${jsonFromPayload.productName}\n`;
    if (jsonFromPayload.customerNumber) autoChatText += `ID meteran: ${jsonFromPayload.customerNumber}\n`;
    if (jsonFromPayload.phoneNumber) autoChatText += `Nomor handphone: ${jsonFromPayload.phoneNumber}\n`;
    if (jsonFromPayload.serialNumber) autoChatText += `Serial number: ${jsonFromPayload.serialNumber}\n`;

    sendTextToChat(autoChatText);
    commit('setAutoChatSentStatus', { autoChatType: 'transactionDetail', status: true, payload: jsonFromPayload });
  },

  /**
   * @param {object} context
   * @param {object} autoChatPayload
   * @param {string | object} autoChatPayload.payload
   * @param {string} autoChatPayload.type
   * @param {string} autoChatPayload.from
   */
  initVPTransactionAutoChat({ state, commit }, autoChatPayload) {
    console.warn('[DEPRECATED] Use initTransactionDetailAutoChat instead');
    const { autoChatSentStatus } = state;
    const { payload, type } = autoChatPayload;

    if (type !== 'queryStringObject' || !payload.productName || autoChatSentStatus.VPtransaction) return;

    const productType = payload.productName.split(' ')[0].toLowerCase() === 'pulsa' ? 'pulsa' : 'paket data';

    const visitorInfoMessage =
      `Ini detail transaksi ${productType} saya ya:\n` +
      `Tanggal transaksi: ${payload.createdTime}\n` +
      `Status: ${payload.status}\n` +
      `Invoice: ${payload.paymentId}\n` +
      `Nama produk: ${payload.productName}\n` +
      `Nomor handphone: ${payload.phoneNumber}\n` +
      `Serial number: ${payload.SN}`;

    sendTextToChat(visitorInfoMessage);
    commit('setAutoChatSentStatus', { autoChatType: 'VPtransaction', status: true, payload });
  },

  /**
   * @param {object} context
   * @param {object} autoChatPayload
   * @param {string | object} autoChatPayload.payload
   * @param {string} autoChatPayload.type
   * @param {string} autoChatPayload.from
   */
  initInAppReviewAutoChat({ state, commit }, autoChatPayload) {
    const { autoChatSentStatus } = state;

    if (autoChatPayload.type !== 'jsonBase64UrlEncoded' || autoChatSentStatus.inAppReview) return;

    const jsonFromPayload = base64UrlToJson(autoChatPayload.payload);
    if (!jsonFromPayload.productType || jsonFromPayload.productType !== 'In App Review') return;

    let ratingStar = '';
    for (let i = 0; i < jsonFromPayload.rating; ++i) {
      ratingStar += '⭐';
    }

    const autoChatText =
      '\n' + `Nilai aplikasi ${ratingStar} (${jsonFromPayload.rating})\n` + '\n' + `Catatan: ${jsonFromPayload.notes}`;

    sendTextToChat(autoChatText);
    commit('setAutoChatSentStatus', { autoChatType: 'inAppReview', status: true, payload: jsonFromPayload });
  },
};
